import React, { useEffect, useState } from "react";
import Seo from "../../components/Seo";

const estimateReadTime = (content) => {
  const wordsPerMinute = 238;
  const wordCount = content.split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return minutes <= 1 ? "1 min read" : `${minutes} min read`;
};

const stripHtml = (value = "") =>
  value
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, "\"")
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const resolveImageUrl = (src) => {
  if (!src) {
    return "https://victorspringer.com/avatar.jpg";
  }

  if (/^https?:\/\//i.test(src)) {
    return src;
  }

  if (src.startsWith("/")) {
    return `https://victorspringer.com${src}`;
  }

  return `https://victorspringer.com/${src.replace(/^\.?\//, "")}`;
};

const extractFirstImage = (html = "") => {
  const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return resolveImageUrl(match ? match[1] : "");
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`/api/blog`);
        if (!response.ok) {
          setPosts([]);
          setIsLoading(false);
          return;
        }
        const data = await response.json();
        setPosts(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching post:", error);
        setPosts([]);
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const formatTimestamp = (timestamp) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(timestamp._seconds * 1000).toLocaleDateString("en-US", options);
  };

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Victor Springer's Tech Blog",
    "url": "https://victorspringer.com/blog",
    "description": "Technical writing by Victor Springer about software engineering, architecture, backend and frontend development, and career growth.",
    "author": {
      "@type": "Person",
      "name": "Victor Springer",
      "url": "https://victorspringer.com/cv",
      "image": "https://victorspringer.com/avatar.jpg",
    }
  };

  const blogPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": "https://victorspringer.com/blog",
    "url": "https://victorspringer.com/blog",
    "name": "Victor Springer Blog",
    "description": "Collection of technical articles by Victor Springer.",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://victorspringer.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog"
      }
    ]
  };

  if (posts.length > 0) {
    blogSchema.blogPost = posts.map((post) => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "author": {
        "@type": "Person",
        "name": post.author || "Victor Springer",
        "url": "https://victorspringer.com/cv",
        "image": "https://victorspringer.com/avatar.jpg",
      },
      "datePublished": post.timestamp ? new Date(post.timestamp._seconds * 1000).toISOString() : "",
      "url": `https://victorspringer.com/blog/${post.slug}`,
      "description": stripHtml(post.content).slice(0, 160),
      "image": extractFirstImage(post.content),
    }));
  }

  return (
    <>
      <Seo
        title="Blog"
        description="Technical articles by Victor Springer about software engineering, architecture, backend and frontend development, and career growth."
        structuredData={[blogSchema, blogPageSchema, breadcrumbSchema]}
      />

      <main className="max-w-4xl mx-auto">
        <section className="md:col-span-2">
          <h1 className="text-dracula-pink text-4xl text-center font-semibold mb-6">
            Blog<span className="cursor"></span>
          </h1>

          {isLoading ? (
            <p className="text-center">
              <span className="spinner border-2 border-t-2 border-dracula-green w-8 h-8"></span>
            </p>
          ) : posts.length === 0 ? (
            <p className="text-dracula-comment text-center">No posts available yet.</p>
          ) : (
            posts.map((post) => {
              const publishedIso = post.timestamp
                ? new Date(post.timestamp._seconds * 1000).toISOString()
                : "";
              return (
                <article
                  key={post.id}
                  className="bg-dracula-currentLine p-6 mb-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
                >
                  <a href={`/blog/${post.slug}`} aria-label={`Read post: ${post.title}`}>
                    <h2 className="text-dracula-yellow text-2xl font-semibold">{post.title}</h2>
                    {post.author && (
                      <p className="text-dracula-comment text-sm mt-1">
                        {estimateReadTime(post.content)}
                        <span className="mx-2">·</span>
                        {publishedIso && (
                          <time dateTime={publishedIso}>{formatTimestamp(post.timestamp)}</time>
                        )}
                      </p>
                    )}
                    <div
                      className="blog-content text-dracula-foreground mt-4 line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: post.content }}
                    />
                  </a>
                  <a href={`/blog/${post.slug}`} className="text-dracula-cyan text-sm mt-3 inline-block hover:text-dracula-green font-bold">
                    Read more →
                  </a>
                </article>
              );
            })
          )}
        </section>
      </main>
    </>
  );
};

export default Blog;
